<template>
  <div>
    <div :id="`recap-card-${genieRecap.id}`" @click="openCommentsAndSideBar" :class="[!comments_tab ? 'cursor-pointer' : '']">
      <div class="mb-2 flex items-center justify-between">
        <div class="flex">
          <Tooltip
            text="See how the Genie summarized this conversation, and share any other thoughts you have on this topic!"
            styleTooltip="top-0 left-full ml-2"
          >
            <div class="flex items-center">
              <img class="avatar mr-3 w-7 h-7 rounded-full" src="/static/roundtable_options_create/genie_icon.svg" />
              <span class="mr-2 text-sm font-semibold font-poppins text-gray-800"> Genie Recap </span>
              <span class="text-xs text-gray-600">{{ recapDateTtimeAgo }}</span>
            </div>
          </Tooltip>
          <Tooltip
            v-if="comments_tab"
            text="Collapse"
            padding="px-3 py-1.5"
            styleTooltip="top-0 left-full -mt-1.5 ml-2"
          >
            <button @click.stop.prevent="collapseGenieCard" class="w-8 h-8 hover:bg-gray-200 rounded-full ml-2">
              <i class="fa-solid fa-angle-up" />
            </button>
          </Tooltip>
        </div>
        <div v-if="genieRecap.id" class="flex items-center">
          <Tooltip
            v-if="genieRecap.submissions_count < submissions"
            :text="`Summary generated from ${genieRecap.submissions_count} of ${submissions} existing submissions. Regenerate to include all submissions.`"
            padding="px-3 py-1.5"
            styleTooltip="top-0 right-full -mt-1.5 mr-2"
          >
            <span class="text-xs text-gray-600">
              Summarized {{ genieRecap.submissions_count }} of
              {{ submissions }} submissions
            </span>
          </Tooltip>
          <span v-else class="text-xs text-gray-600">
            Summarized {{ genieRecap.submissions_count }} of
            {{ submissions }} submissions
          </span>
          <pl-menu
            v-if="hasTeacherPermissionsInCourse"
            direction="right"
            :menuSize="3"
            class="ml-1 z-10"
          >
            <button slot="menu_trigger" class="w-8 h-8 rounded-full hover:bg-blueGray-100">
              <i class="fas fa-ellipsis-v text-sm text-gray-500" />
            </button>
            <ul menu-content class="px-1 py-1">
              <li @click="editRecap()" class="sample rounded py-1.5 px-2.5 cursor-pointer w-full flex space-x-2 items-center hover:bg-coolGray-100">
                <span style="font-size: 14px" class="text-blueGray-700">Edit Recap</span>
              </li>
              <li @click="handleDeleteRecap()" class="sample rounded py-1.5 px-2.5 cursor-pointer w-full flex space-x-2 items-center hover:bg-red-50">
                <span style="font-size: 14px" class="text-red-500">Delete</span>
              </li>
            </ul>
          </pl-menu>
        </div>
      </div>
      <div class="ml-11 ">
        <div>
          <div>
            <div>
              <div v-if="Object.keys(genieRecap).length > 0 && genieRecap.prompt_summary !== '' && genieRecap.questions.length > 0">
                <h6 class="mb-4 font-poppins font-semibold text-blueGray-800 antialiased">
                  Here's a summary of all the different ideas in our class:
                </h6>
                <div :class="loading ? 'line-through' : ''" v-html="genieRecap.prompt_summary"></div>
              </div>
              <h6 class="mb-4 text-blueGray-800 antialiased" v-else>
                Ask the Genie to summarize insights from all
                {{ submissions }} of the submissions in this RoundTable!
              </h6>
            </div>
            <div>
              <div v-if="hasTeacherPermissionsInCourse && (Object.keys(genieRecap).length === 0 || loading === true)">
                <button
                  class="sample flex space-x-2.5 items-center px-5 py-2 bg-pink-50 border border-solid border-pink-300 rounded text-xs text-pinky font-semibold hover:bg-pinky-new_light"
                  @click.stop.prevent="generateGenieRecap"
                  :disabled="loading"
                >
                  <div v-if="loading" class="loader -ml-1.5"></div>
                  <span>{{ loading ? 'Generating...' : 'Generate Recap' }}</span>
                </button>
              </div>
              <div v-else>
                <div class="flex pt-5">
                  <button
                    id="recap-comments"
                    @click.stop.prevent="getRecapComments"
                    class="sample mr-2 px-5 py-2 bg-gray-100 border-blueGray-300 border hover:bg-blueGray-600 hover:border-blueGray-600 hover:text-blueGray-100 border-solid rounded text-xs font-semibold"
                    :class="[comments_tab ? 'bg-blueGray-600 text-blueGray-100 border-blueGray-600' : 'text-gray-400']"
                  >
                    Comments ({{ genieRecap.comment_count }})
                  </button>
                  <button
                    v-if="hasTeacherPermissionsInCourse && genieRecap.submissions_count < submissions"
                    class="sample flex space-x-2.5 items-center px-5 py-2 bg-pink-50 border border-solid border-pink-300 rounded text-xs text-pinky font-semibold hover:bg-pinky-new_light"
                    :disabled="loading"
                    @click.stop.prevent="regenerateSummary"
                  >
                    <div v-if="loading" class="loader -m-1.5"></div>
                    <span>{{ loading ? genieRecap.recap_created ? 'Generating...' : 'Regenerating...' : 'Regenerate' }}</span>
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
        <CommentsTab
          v-show="comments_tab"
          :key="`recap-comment-${genieRecap.id}`"
          :fromGenieRecap="true"
          :recapCommentsFromService="recap_comments"
          :isFromNotification="is_from_notification"
          class="mt-5"
        />
        <div v-if="comments_tab" class="my-6 flex justify-center">
          <button
            key="collapse"
            @click.stop.prevent="collapseGenieCard"
            class="bg-white text-blueGray-500 hover:border-blueGray-700 hover:text-blueGray-700 border border-solid border-blueGray-500 rounded-full px-4 py-1"
          >
            <i class="fa-solid fa-angle-up"></i>
          </button>
        </div>
      </div>
    </div>
  </div>
</template>
<script>
  import { EventBus } from 'utils/event-bus';
  import moment from 'moment';
  import { mapGetters } from 'vuex';
  import CommentsTab from 'features/onlineRoundtables/components/commentsTab.vue';
  import DialogDeleteConfirm from 'components/dialogDeleteConfirm';
  export default {
    data() {
      return {
        comments_tab: false,
        recap_comments: [],
        loading: false,
        is_from_notification: false,
      };
    },
    components: {
      CommentsTab,
      DialogDeleteConfirm,
      Tooltip: () => import('../../../../components/Tooltip.vue'),
    },
    props: {
      submissions: {
        type: Number,
        default: 0,
      },
    },
    computed: {
      ...mapGetters(['hasTeacherPermissionsInCourse', 'getCurrentRT']),
      genieRecap() {
        return this.getCurrentRT.genie_recap;
      },
      recapDateTtimeAgo() {
        return this.genieRecap.updated_at
          ? moment(this.genieRecap.updated_at, moment.ISO_8601).fromNow()
          : null;
      },
      isFromNotificationState() {
        return this.$store.state.isFromNotification;
      },
    },
    methods: {
      handleDeleteRecap() {
        this.comments_tab = false;
        this.$emit('deleteRecap');
      },
      async openCommentsAndSideBar() {
        if (this.genieRecap.id) {
          await this.getRecapComments();
          if (!this.comments_tab) {
            this.comments_tab = !this.comments_tab;
          }
          if (this.hasTeacherPermissionsInCourse) {
            EventBus.$emit('generate-genie-recap');
          }
        }
      },
      collapseGenieCard() {
        if (this.$route.query) {
          this.$router.replace({ query: null });
        }
        this.comments_tab = false;
      },
      async generateGenieRecap() {
        this.loading = true;
        try {
          const recap = await this.$store
            .dispatch('createGenieRecap', {
              roundtable_id: this.getCurrentRT.id,
            })
            .catch((error) => {
              const toast = {
                type: 'Error',
                message: `Sorry! Something wrong happenned on creating Genie Recap. ${error}`,
                time: 5000,
              };
              return EventBus.$emit('createToast', toast);
            });
          await Promise.all([
            this.$store.dispatch('genieRecapSummarizeSubmissions', {
              id: recap.id,
              roundtable_id: this.getCurrentRT.id,
            }),
            this.$store.dispatch('genieRecapGenerateFollowUpQuestions', {
              id: recap.id,
              roundtable_id: this.getCurrentRT.id,
            }),
          ]).then((data) => {
            if (data) {
              if (this.hasTeacherPermissionsInCourse) {
                EventBus.$emit('generate-genie-recap', recap);
              }
              this.loading = false;
            }
          });
          return;
        } catch (error) {
          const toast = {
            type: 'Error',
            message:
              'Sorry! Something wrong happenned on generating Recap! Try again!',
            time: 5000,
          };
          EventBus.$emit('createToast', toast);
        }
      },
      regenerateSummary() {
        this.loading = true;
        this.$store
          .dispatch('genieRecapSummarizeSubmissions', {
            id: this.genieRecap.id,
            roundtable_id: this.getCurrentRT.id,
          })
          .then(() => {
            const toast = {
              type: 'Success',
              message: 'Genie Recap summary successfully regenerated.',
              time: 5000,
            };
            EventBus.$emit('createToast', toast);
            if (this.hasTeacherPermissionsInCourse) {
              EventBus.$emit('generate-genie-recap');
            }
          })
          .catch((err) => {
            console.error(err);
            const toast = {
              type: 'Error',
              message:
                'Sorry! Something happenned on regenerating summary! Try again!',
              time: 5000,
            };
            EventBus.$emit('createToast', toast);
          })
          .finally(() => (this.loading = false));
      },
      getRecapComments() {
        if (this.genieRecap.id && !this.genieRecap.comments) {
          this.$store
            .dispatch('getRecapComments', {
              id: this.genieRecap.id,
              roundtable_id: this.getCurrentRT.id,
            })
            .then((data) => (this.recap_comments = data))
            .catch((err) => console.error(err))
            .finally(() => {
              if (this.hasTeacherPermissionsInCourse) {
                EventBus.$emit('generate-genie-recap');
              }
            });
        }
        this.comments_tab = !this.comments_tab;
      },
      editRecap() {
        this.$emit('editRecap');
      },
      async isMountedFromNotification() {
        if (
          this.genieRecap.id &&
          this.genieRecap.id === this.$route.query.recapID &&
          this.isFromNotificationState
        ) {
          await this.getRecapComments();
          this.comments_tab = true;
          this.is_from_notification = true;
        }
      },
      regenerateSummaryAndQuestions() {
        if (this.genieRecap && this.genieRecap.id) {
          this.loading = true;
          this.$store
            .dispatch('genieRecapSummarizeSubmissions', {
              id: this.genieRecap.id,
              roundtable_id: this.getCurrentRT.id,
            })
            .then((data) => {
              const payload = { summary: data.prompt_summary };
              this.$store
                .dispatch('genieRecapGenerateFollowUpQuestions', {
                  id: this.genieRecap.id,
                  roundtable_id: this.getCurrentRT.id,
                  payload,
                })
                .then(() => {
                  const toast = {
                    type: 'Success',
                    message: 'Follow up questions successfully created.',
                    time: 5000,
                  };
                  EventBus.$emit('createToast', toast);
                  if (this.hasTeacherPermissionsInCourse) {
                    EventBus.$emit('generate-genie-recap');
                  }
                  EventBus.$emit('GenieRecapSideBarloading');
                })
                .catch((err) => {
                  console.error(err);
                  const toast = {
                    type: 'Error',
                    message:
                      'Sorry! Something wrong happenned on generating follow up questions! Try again!',
                    time: 5000,
                  };
                  EventBus.$emit('createToast', toast);
                  EventBus.$emit('GenieRecapSideBarloading');
                });
            })
            .catch((err) => {
              console.error(err);
              const toast = {
                type: 'Error',
                message:
                  'Sorry! Something wrong happenned on generating summary.',
                time: 5000,
              };
              EventBus.$emit('createToast', toast);
            })
            .finally(() => (this.loading = false));
        }
      },
    },
    mounted() {
      this.isMountedFromNotification();

      EventBus.$on('scrollToRecap', async (recapId, query) => {
        if (recapId === '') return;
        const comment_id = query.topLevelCommentID;
        const reply_id = query.replyCommentID;
        await this.getRecapComments();
        this.comments_tab = true;
        if (comment_id && reply_id) {
          EventBus.$emit('scrollToRecapReplyComment', comment_id);
        } else {
          setTimeout(() => {
            const comment_element = document.getElementById(
              `individual-comment-${comment_id}`
            );
            if (comment_element) {
              comment_element.scrollIntoView({
                block: 'start',
                inline: 'nearest',
                behavior: 'smooth',
              });
            }
          }, 500);
        }
      });
      EventBus.$on('open-genie-recap', async () => {
        await this.getRecapComments();
        this.comments_tab = true;
        if (this.hasTeacherPermissionsInCourse) {
          EventBus.$emit('sidebar-recap', true);
        }
      });
      EventBus.$on('regenerateSummaryAndQuestions', () => {
        this.regenerateSummaryAndQuestions();
      });
    },
    beforeDestroy() {
      EventBus.$off('scrollToRecap');
      EventBus.$off('open-genie-recap');
      EventBus.$off('regenerateSummaryAndQuestions');
    },
  };
</script>
<style scoped>
  .loader {
    border-top: 2px solid #eb2d7c;
    border-right: 2px solid #eb2d7c;
    border-bottom: 2px solid #eb2d7c;
    border-left: 2px solid #ff82b6;
    -webkit-transform: translateZ(0);
    -ms-transform: translateZ(0);
    transform: translateZ(0);
    -webkit-animation: load8 1.1s infinite linear;
    animation: load8 1.1s infinite linear;
  }
</style>



// WEBPACK FOOTER //
// genieRecap.vue?7e00994c